import {
  now,
  offsetDateTimeToMs,
  formatOffsetDateTime,
} from "@freightsimple/time/helpers";
import type { OffsetDateTime } from "@freightsimple/time/branded-types";
import { Tooltip } from "../../Feedback/Tooltip";
import {
  formatRelativeLabelShort,
  formatPreciseDuration,
  getRelativeColor,
  badgeColorStyles,
} from "../Internal/relativeTime";
import type { RelativeMode } from "../Internal/relativeTime";

const timestampFormats: Record<string, string> = {
  iso: "",
  short: "MMM D, YYYY",
  medium: "MMM D, YYYY h:mm A",
  long: "ddd, MMM D, YYYY [at] h:mm A z",
  timeOnly: "h:mm A",
  time24: "HH:mm",
};

type TimestampFormat =
  | "iso"
  | "short"
  | "medium"
  | "long"
  | "timeOnly"
  | "time24"
  | (string & {});

interface TimestampProps {
  /** ISO timestamp to display */
  value: OffsetDateTime | undefined;
  /** Format preset or custom dayjs format string (default: "medium") */
  format?: TimestampFormat | undefined;
  /**
   * Show absolute + relative badge.
   * - `true` — muted badge (for events: "3h ago")
   * - `"age"` — green→gold→red as time passes
   * - `"deadline"` — green when far, gold when close, red when overdue
   */
  relative?: RelativeMode | undefined;
  /**
   * Show relative badge only, no absolute timestamp.
   * - `true` — muted badge
   * - `"age"` — green→gold→red as time passes
   * - `"deadline"` — green→gold→red as deadline approaches
   */
  relativeOnly?: RelativeMode | undefined;
  /** Size of the relative badge. Defaults to "small". */
  relativeBadgeSize?: "small" | "wide" | undefined;
  /** Drop the "ago"/"in" suffix from the badge label. Use when the column header (e.g. "Open For") already implies direction. */
  relativeNoSuffix?: boolean | undefined;
}

function resolveStrategy(mode: RelativeMode) {
  return mode === true ? undefined : mode;
}

const relativeBadgeSizeStyles = {
  small: "px-1.5 py-px text-[10px]",
  wide: "px-2.5 py-0.5 text-xs",
};

function RelativeBadge_({
  value,
  mode,
  size = "small",
  noSuffix,
}: {
  value: OffsetDateTime;
  mode: RelativeMode;
  size?: "small" | "wide" | undefined;
  noSuffix?: boolean | undefined;
}) {
  const nowMs = offsetDateTimeToMs(now());
  const targetMs = offsetDateTimeToMs(value);
  const ms = nowMs - targetMs;
  const label = formatRelativeLabelShort(ms, noSuffix);
  const color = getRelativeColor(ms, resolveStrategy(mode));

  return (
    <Tooltip
      content={
        <>
          <div className="font-bold">
            {formatOffsetDateTime(value, "dddd, MMMM D, YYYY h:mm:ss A")}
          </div>
          <div className="mt-0.5 text-popover-foreground/60">
            {formatPreciseDuration(ms)}
          </div>
        </>
      }
    >
      <span
        className={`inline-flex cursor-default items-center rounded border font-bold ${relativeBadgeSizeStyles[size ?? "small"]} ${badgeColorStyles[color]}`}
      >
        {label}
      </span>
    </Tooltip>
  );
}

export function Timestamp({
  value,
  format = "medium",
  relative,
  relativeOnly,
  relativeBadgeSize,
  relativeNoSuffix,
}: TimestampProps) {
  if (!value) return <span>{"\u2014"}</span>;

  if (relativeOnly) {
    return (
      <RelativeBadge_
        value={value}
        mode={relativeOnly}
        size={relativeBadgeSize}
        noSuffix={relativeNoSuffix}
      />
    );
  }

  const formatted =
    format === "iso"
      ? value
      : formatOffsetDateTime(
          value,
          timestampFormats[format] ?? format,
          "\u2014",
        );

  if (relative) {
    return (
      <span className="inline-flex items-center gap-1">
        <span>{formatted}</span>
        <RelativeBadge_
          value={value}
          mode={relative}
          size={relativeBadgeSize}
          noSuffix={relativeNoSuffix}
        />
      </span>
    );
  }

  return <span>{formatted}</span>;
}
